import './index.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import environment from './environment.ts';
import AnalyticsProvider from './contexts/analytics/analytics-provider.tsx';
import { APIProvider as GoogleApiProvider } from '@vis.gl/react-google-maps';
import { BrandingProvider } from './contexts/branding/branding-provider';
import { defaultEventPrefix } from './services/analytics-service.ts';
import { initDatadog } from './services/datadog-service.ts';
import * as Sentry from '@sentry/react';

type RootOptions = Parameters<typeof createRoot>[1];

let options: RootOptions;

if (environment.sentryEnabled) {
  Sentry.init({
    dsn: environment.sentryDsn,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    release: environment.releaseSha,
    environment: environment.environment,
  });

  options = {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  };
}

if (environment.dataDogEnabled) {
  initDatadog(environment);
}

const root = createRoot(document.getElementById('root')!, options);

root.render(
  <StrictMode>
    <AnalyticsProvider eventPrefix={defaultEventPrefix}>
      <GoogleApiProvider apiKey={environment.googleMapsApiKey}>
        <BrandingProvider>
          <App />
        </BrandingProvider>
      </GoogleApiProvider>
    </AnalyticsProvider>
  </StrictMode>
);
